/**
 * Event-type presets — the core of the adaptive-app pivot.
 *
 * Maps an event's `event_type` slug (from `lib/api.js` → `eventTypeGroups`)
 * to a "family" and a tab configuration. The tab config controls both
 * ORDERING and LABELS across every host that consumes it (EventDetail top
 * nav + CommandMode sidebar).
 *
 * Data model stays unified: every family reads/writes the SAME collections
 * (members, timeline_blocks, budget_items…). Only the labels + visibility
 * flags change. A "Team" for a sports tournament is stored as a `member` with
 * role=team; a "Bracket entry" is a `timeline_block`. No new tables.
 */

// All possible tab keys — labels/hidden flags override per-family.
// Keys MUST match the switch/case values in EventDetail.jsx.
export const ALL_TAB_KEYS = [
  "overview", "budget", "vendors", "tasks", "timeline", "rehearsal",
  "guests", "seating", "rsvp", "documents", "chat", "setup", "live",
];

// Group-label (from api.js eventTypeGroups) → family key. Everything else
// falls back to "wedding" (our historical default).
const GROUP_TO_FAMILY = {
  "Weddings & Milestones": "wedding",
  "Private Celebrations":  "private",
  "Children's Events":     "private",
  "Corporate":             "corporate",
  "Community":             "community",
  "Sports & Recreation":   "tournament",
  "Entertainment":         "production",
  "Faith-Based":           "community",
  "Fundraising":           "fundraiser",
  "Educational":           "corporate",
  "Outdoor & Adventure":   "community",
  "Hobby & Interest":      "community",
  "Food & Beverage":       "community",
  "Nightlife":             "production",
  "Seasonal":              "community",
};

/**
 * Every family defines the tabs it wants to expose, in display order.
 * Any tab key omitted from `tabs` is hidden. The `label` overrides the
 * default label. `live` is a boolean the host renders as a LIVE pulse.
 */
export const EVENT_FAMILIES = {
  wedding: {
    label: "Wedding",
    tabs: [
      { key: "overview",  label: "Event Brief" },
      { key: "timeline",  label: "Timeline" },
      { key: "rehearsal", label: "Rehearsal" },
      { key: "guests",    label: "People" },
      { key: "vendors",   label: "Vendors" },
      { key: "tasks",     label: "Tasks" },
      { key: "budget",    label: "Finance" },
      { key: "rsvp",      label: "RSVP & Registry" },
      { key: "seating",   label: "Floor Plan" },
      { key: "documents", label: "Documents" },
      { key: "chat",      label: "Communications" },
      { key: "setup",     label: "Setup & Strike" },
      { key: "live",      label: "Live Command", live: true },
    ],
  },
  tournament: {
    label: "Sports Tournament",
    tabs: [
      { key: "overview",  label: "Event Brief" },
      { key: "timeline",  label: "Schedule" },
      { key: "guests",    label: "Teams" },
      { key: "vendors",   label: "Officials & Sponsors" },
      { key: "tasks",     label: "Tasks" },
      { key: "budget",    label: "Revenue" },
      { key: "seating",   label: "Field / Court Plan" },
      { key: "documents", label: "Documents" },
      { key: "chat",      label: "Communications" },
      { key: "setup",     label: "Setup & Strike" },
      { key: "live",      label: "Live Command", live: true },
    ],
  },
  corporate: {
    label: "Corporate Event",
    tabs: [
      { key: "overview",  label: "Event Brief" },
      { key: "timeline",  label: "Agenda" },
      { key: "guests",    label: "Attendees" },
      { key: "vendors",   label: "Speakers & Vendors" },
      { key: "tasks",     label: "Tasks" },
      { key: "budget",    label: "Finance" },
      { key: "seating",   label: "Floor Plan" },
      { key: "documents", label: "Documents" },
      { key: "chat",      label: "Communications" },
      { key: "setup",     label: "Setup & Strike" },
      { key: "live",      label: "Live Command", live: true },
    ],
  },
  community: {
    label: "Community Event",
    tabs: [
      { key: "overview",  label: "Event Brief" },
      { key: "timeline",  label: "Timeline" },
      { key: "guests",    label: "Attendees" },
      { key: "vendors",   label: "Volunteers & Vendors" },
      { key: "tasks",     label: "Tasks" },
      { key: "budget",    label: "Finance" },
      { key: "seating",   label: "Site Plan" },
      { key: "documents", label: "Permits & Documents" },
      { key: "chat",      label: "Communications" },
      { key: "setup",     label: "Setup & Strike" },
      { key: "live",      label: "Live Command", live: true },
    ],
  },
  production: {
    label: "Production / Show",
    tabs: [
      { key: "overview",  label: "Event Brief" },
      { key: "timeline",  label: "Run of Show" },
      { key: "guests",    label: "Crew" },
      { key: "vendors",   label: "Talent & Vendors" },
      { key: "tasks",     label: "Tasks" },
      { key: "budget",    label: "Finance" },
      { key: "seating",   label: "Stage Plot" },
      { key: "documents", label: "Documents" },
      { key: "chat",      label: "Communications" },
      { key: "setup",     label: "Setup & Strike" },
      { key: "live",      label: "Live Command", live: true },
    ],
  },
  fundraiser: {
    label: "Fundraiser",
    tabs: [
      { key: "overview",  label: "Event Brief" },
      { key: "timeline",  label: "Program" },
      { key: "guests",    label: "Donors & Guests" },
      { key: "vendors",   label: "Sponsors & Vendors" },
      { key: "tasks",     label: "Tasks" },
      { key: "budget",    label: "Finance" },
      { key: "rsvp",      label: "RSVP" },
      { key: "seating",   label: "Floor Plan" },
      { key: "documents", label: "Documents" },
      { key: "chat",      label: "Communications" },
      { key: "setup",     label: "Setup & Strike" },
      { key: "live",      label: "Live Command", live: true },
    ],
  },
  private: {
    label: "Private Party",
    tabs: [
      { key: "overview",  label: "Event Brief" },
      { key: "timeline",  label: "Timeline" },
      { key: "guests",    label: "People" },
      { key: "vendors",   label: "Vendors" },
      { key: "tasks",     label: "Tasks" },
      { key: "budget",    label: "Finance" },
      { key: "rsvp",      label: "RSVP" },
      { key: "seating",   label: "Floor Plan" },
      { key: "documents", label: "Documents" },
      { key: "chat",      label: "Communications" },
      { key: "setup",     label: "Setup & Strike" },
      { key: "live",      label: "Live Command", live: true },
    ],
  },
};

/** Family key from an event object (falls back to "wedding"). */
export function familyForEvent(ev) {
  if (!ev) return "wedding";
  const groupLabel = ev.event_type_group;
  if (groupLabel && GROUP_TO_FAMILY[groupLabel]) return GROUP_TO_FAMILY[groupLabel];
  // Fallback: parse from the slug when the group isn't on the record. Every
  // slug in `eventTypeGroups` belongs to exactly one group; try to match on
  // a small set of substrings so we don't need to import the full flat list.
  const slug = String(ev.event_type || "").toLowerCase();
  if (!slug) return "wedding";
  if (/tournament|race|meet|competition|marathon/.test(slug)) return "tournament";
  if (/conference|seminar|banquet|networking|expo|trade-show/.test(slug)) return "corporate";
  if (/festival|parade|fair|community|block-party|farmers/.test(slug)) return "community";
  if (/concert|show|comedy|dj|dance|karaoke|talent/.test(slug)) return "production";
  if (/fundraiser|gala|auction|benefit|5k/.test(slug)) return "fundraiser";
  if (/wedding|elopement|vow|rehearsal-dinner|bridal-shower|bachelor|bachelorette|engagement/.test(slug)) return "wedding";
  if (/birthday|shower|reunion|holiday-party|memorial|graduation|retirement|housewarming|reveal/.test(slug)) return "private";
  return "wedding";
}

/**
 * Tab config for a given event. Consumers get an ordered array of
 * { key, label, live? } — the host renders exactly what's returned.
 */
export function tabsForEvent(ev) {
  const family = familyForEvent(ev);
  return EVENT_FAMILIES[family]?.tabs || EVENT_FAMILIES.wedding.tabs;
}

/** Human-readable name for the family (used on the Event Brief chip). */
export function familyLabel(ev) {
  const key = familyForEvent(ev);
  return EVENT_FAMILIES[key]?.label || "Event";
}
